import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { LogIn, Terminal, Loader2, KeyRound, ArrowLeft, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("fminoia@lojack.it");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Password recovery state
  const [showRecovery, setShowRecovery] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState(["", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data.success) {
        toast.success("Accesso effettuato!");
        onLogin(response.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  const openRecoveryDialog = async () => {
    try {
      const response = await axios.get(`${API}/auth/security-questions`);
      setQuestions(response.data);
      setAnswers(["", "", ""]);
      setNewPassword("");
      setConfirmPassword("");
      setShowRecovery(true);
    } catch (error) {
      toast.error("Errore nel caricamento delle domande di sicurezza");
    }
  };

  const handleRecovery = async () => {
    if (answers.some(a => !a.trim())) {
      toast.error("Rispondi a tutte le domande");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }

    setRecoveryLoading(true);
    try {
      const response = await axios.post(`${API}/auth/recover-password`, {
        answers,
        new_password: newPassword
      });
      if (response.data.success) {
        toast.success("Password reimpostata con successo!");
        setShowRecovery(false);
        setPassword(newPassword);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Risposte non corrette");
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Grid pattern background */}
      <div className="absolute inset-0 grid-pattern opacity-30" />
      
      {/* Subtle glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <Card className="w-full max-w-sm md:max-w-md border-border bg-card/80 backdrop-blur-sm relative z-10" data-testid="login-card">
        <CardHeader className="space-y-3 md:space-y-4 text-center pb-2">
          <div className="flex items-center justify-center gap-2 text-primary">
            <Terminal className="w-6 h-6 md:w-8 md:h-8" />
            <span className="text-xl md:text-2xl font-bold tracking-tight font-['Manrope']">TASK_CMD //</span>
          </div>
          <CardTitle className="text-lg md:text-xl font-semibold">Benvenuto</CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            Task Management per Federico Minoia
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="fminoia@lojack.it"
                className="bg-secondary border-border h-10 text-sm"
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-secondary border-border h-10 text-sm"
                data-testid="password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-10" 
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <LogIn className="w-4 h-4 mr-2" />
              )}
              Accedi
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Button 
              variant="link" 
              className="text-xs text-muted-foreground hover:text-primary p-0 h-auto"
              onClick={openRecoveryDialog}
              data-testid="forgot-password-btn"
            >
              <KeyRound className="w-3 h-3 mr-1" />
              Password dimenticata?
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Password Recovery Modal */}
      {showRecovery && (
        <div className="fixed inset-0 z-50">
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/80"
            onClick={() => setShowRecovery(false)}
          />
          
          {/* Content */}
          <div className="fixed left-[50%] top-[50%] z-50 w-[calc(100%-2rem)] sm:max-w-[450px] max-h-[90vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] bg-card border border-border p-6 shadow-lg rounded-lg" data-testid="recovery-dialog">
            {/* Close button */}
            <button
              type="button"
              onClick={() => setShowRecovery(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
            >
              <X className="h-4 w-4" />
            </button>
            
            <div className="flex flex-col space-y-1.5 text-center sm:text-left mb-4">
              <h2 className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-primary" />
                Recupera Password
              </h2>
              <p className="text-sm text-muted-foreground">
                Rispondi alle domande di sicurezza per reimpostare la password
              </p>
            </div>
            
            <div className="space-y-4 py-4">
              {questions.map((q, index) => (
                <div key={q.id} className="space-y-2">
                  <Label className="text-sm">{q.question}</Label>
                  <Input
                    value={answers[index]}
                    onChange={(e) => {
                      const newAnswers = [...answers];
                      newAnswers[index] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                    placeholder="La tua risposta..."
                    className="bg-secondary h-9 text-sm"
                    data-testid={`security-answer-${index}`}
                  />
                </div>
              ))}
              
              <div className="border-t border-border pt-4 mt-4 space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm">Nuova Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimo 6 caratteri"
                    className="bg-secondary h-9 text-sm"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Conferma Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Ripeti la password"
                    className="bg-secondary h-9 text-sm"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowRecovery(false)} className="w-full sm:w-auto">
                Annulla
              </Button>
              <Button onClick={handleRecovery} disabled={recoveryLoading} data-testid="recover-btn" className="w-full sm:w-auto">
                {recoveryLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Reimposta Password
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* TelcaVoIP Badge */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
        <a 
          href="https://telcavoip.ai" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <span>Made With</span>
          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
          <span>By <strong className="text-primary">TelcaVoIP AI</strong></span>
        </a>
      </div>
    </div>
  );
}
